/*
 * The cart drawer's top progress bar as the gift ladder: a dot per gift tier on the bar that
 * snippets/discount-progress-bar.liquid already renders, the same bar as the add-to-cart
 * toast (nb-cart-toast.js). Nothing is added to the drawer - no panel, no extra text - the
 * existing bar is dressed up.
 *
 * Rungs come from theme.nbGift.steps() (nb-free-gift.js: the gift tiers plus free shipping,
 * six by default) and the paid-bag count from the bar's data-nb-paid, so the bar steps
 * evenly from rung to rung instead of running to a fixed ten bags. Without the gift engine the plain server-rendered bar stays as it is.
 *
 * The drawer HTML is replaced on every cart render, so each new bar starts from where the
 * last one stood and moves on from there; when the drawer opens it fills from empty.
 * Styles: assets/nb-cart-ladder.css (.nb-ladder). Loaded from sections/cart-drawer.liquid, only
 * while its "NB12 test" switch is on - never from the layout, never from custom.css.
 */
(function () {
  var drawer = document.getElementById('CartDrawer');
  if (!drawer || (window.Shopify && Shopify.designMode)) return;

  var last = { fill: 0, reached: 0 }; // where the bar stood after the previous render
  var renderRaf = 0;
  var openTimer = 0;

  function isOpen() { return drawer.hasAttribute('open'); }

  function state(box) {
    var tiers = window.theme && theme.nbGift && theme.nbGift.steps
      ? theme.nbGift.steps().map(function (s) { return s.at; })
      : [];
    var paid = parseInt(box.getAttribute('data-nb-paid'), 10);
    if (!tiers.length || isNaN(paid)) return null;
    // One even step per tier, first dot on the bar's start and last on its end (as the toast)
    var end = tiers.length - 1;
    var step = function (i) { return end ? i / end : 1; };
    var fill = 0;
    tiers.forEach(function (t, i) {
      if (paid < t) return;
      var next = tiers[i + 1];
      fill = step(i) + (next ? Math.min((paid - t) / (next - t), 1) * (step(i + 1) - step(i)) : 0);
    });
    return { fill: fill, reached: tiers.filter(function (t) { return paid >= t; }).length, count: tiers.length, step: step };
  }

  function set(box, fill, reached, from) {
    var turn = 0; // dots filling in this pass go one after another, 150ms apart
    box.querySelector('.progress-bar').style.width = fill * 100 + '%';
    // Green is reserved for "you earned something", so the bar only turns green once
    // every tier is unlocked; while it is filling it stays neutral. Without this the
    // drawer carried six green elements at once and green stopped meaning anything -
    // the same way Rune described red: "it loses all meaning."
    box.classList.toggle('nb-ladder--complete', reached >= box.querySelectorAll('.nb-ladder__dot').length);
    box.querySelectorAll('.nb-ladder__dot').forEach(function (dot, i) {
      var done = i < reached;
      dot.style.setProperty('--nb-d', (done && i >= from ? turn++ : 0) * 150 + 'ms');
      dot.classList.toggle('is-done', done);
      // the last dot reached pulses, until every tier is unlocked
      dot.classList.toggle('is-current', i === reached - 1 && reached < box.querySelectorAll('.nb-ladder__dot').length);
      // the rung being worked towards carries a green ring
      dot.classList.toggle('is-next', i === reached);
    });
  }

  // The sentence above the bar is one line (Amit, 13 Oct). CSS sizes it from the drawer's
  // width, which covers the English copy; a longer translation can still be wider than the
  // drawer, so it is stepped down to a 10px floor and only then allowed to wrap. Without
  // this a nowrap line is simply cut off mid-word, which is what the old bar did.
  function fitMessage(box) {
    var msg = box.parentElement && box.parentElement.querySelector('.progress-bar-msg');
    if (!msg) return;
    msg.classList.remove('nb-msg--wrap');
    msg.style.fontSize = '';
    var size = parseFloat(getComputedStyle(msg).fontSize);
    var guard = 0;
    while (msg.scrollWidth > msg.clientWidth && size - 0.5 >= 10 && guard++ < 20) {
      size -= 0.5;
      msg.style.fontSize = size + 'px';
    }
    if (msg.scrollWidth > msg.clientWidth) {
      msg.style.fontSize = '';
      msg.classList.add('nb-msg--wrap');
    }
  }

  // Dress a freshly rendered bar: add the dots, stand it where the last one stood, then move on.
  function dress(fromEmpty) {
    var box = drawer.querySelector('.progress-bar-container[data-nb-paid]');
    var bar = box && box.querySelector('.progress-bar');
    if (!box) last = { fill: 0, reached: 0 }; // an empty cart has no bar: the next one starts from nothing
    var s = box && bar && state(box);
    if (!s) return;
    if (!box.classList.contains('nb-ladder')) {
      box.classList.add('nb-ladder');
      for (var i = 0; i < s.count; i++) {
        var dot = document.createElement('span');
        dot.className = 'nb-ladder__dot';
        dot.style.insetInlineStart = s.step(i) * 100 + '%';
        box.appendChild(dot);
      }
    }
    fitMessage(box);
    var from = fromEmpty ? { fill: 0, reached: 0 } : last;
    last = { fill: s.fill, reached: s.reached };
    box.classList.add('nb-ladder--still'); // no transitions while it takes the starting position
    set(box, from.fill, from.reached, from.reached);
    void box.offsetWidth;
    box.classList.remove('nb-ladder--still');
    clearTimeout(openTimer);
    openTimer = 0;
    if (fromEmpty) {
      openTimer = setTimeout(function () {
        openTimer = 0;
        set(box, s.fill, s.reached, 0);
      }, 100);
    } else {
      set(box, s.fill, s.reached, from.reached);
    }
  }

  // Any element coming in is a render (also one that leaves no bar: an emptied cart). The
  // delivery estimate rewrites a text node every frame; text nodes and our own dots are skipped.
  new MutationObserver(function (records) {
    var rendered = records.some(function (m) {
      return Array.prototype.some.call(m.addedNodes, function (n) {
        return n.nodeType === 1 && !n.classList.contains('nb-ladder__dot');
      });
    });
    if (!rendered || renderRaf) return;
    renderRaf = requestAnimationFrame(function () {
      renderRaf = 0;
      dress(!!openTimer); // a render that lands while the bar is still waiting to fill keeps it empty
    });
  }).observe(drawer, { childList: true, subtree: true });

  new MutationObserver(function () {
    if (isOpen()) dress(true);
  }).observe(drawer, { attributes: true, attributeFilter: ['open'] });

  // The server-rendered bar on page load; nb-free-gift.js may boot a moment after this script.
  dress(false);
  setTimeout(function () {
    if (!drawer.querySelector('.nb-ladder')) dress(false);
  }, 1200);
})();
